import React from 'react';
import { Link } from 'react-router-dom';
import './Software.css';

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconSliders = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
    <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
    <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/>
    <line x1="17" y1="16" x2="23" y2="16"/>
  </svg>
);

const IconDatabase = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <ellipse cx="12" cy="5" rx="9" ry="3"/>
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);

const IconLink = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

// ── Software data ─────────────────────────────────────────────────────────────

interface SoftwareProduct {
  id: string;
  title: string;
  tagline: string;
  image: string;
  imageAlt: string;
  features: string[];
}

const softwareProducts: SoftwareProduct[] = [
  {
    id: 'control',
    title: 'Software de Control',
    tagline: 'Sistemas de control para equipos de pesaje automatizados con interfaz intuitiva y configuración flexible por proceso.',
    image: '/images/sw-erp.jpg',
    imageAlt: 'Panel de control industrial con pantalla de monitoreo',
    features: [
      'Interfaz intuitiva y fácil de operar',
      'Control en tiempo real de múltiples básculas',
      'Configuración flexible por proceso productivo',
      'Alarmas y alertas automáticas configurables',
    ],
  },
  {
    id: 'datos',
    title: 'Gestión de Datos',
    tagline: 'Recopilación, almacenamiento y análisis de datos de pesaje para la toma de decisiones informada.',
    image: '/images/sw-data.jpg',
    imageAlt: 'Análisis de datos y reportes de producción industrial',
    features: [
      'Base de datos robusta y segura',
      'Reportes personalizados por turno y proceso',
      'Análisis estadístico de producción',
      'Trazabilidad completa por lote o producto',
    ],
  },
];

// ── Page ──────────────────────────────────────────────────────────────────────

const Software: React.FC = () => {
  return (
    <div className="sw-page">

      {/* ── Hero ── */}
      <section className="sw-hero">
        <div className="container sw-hero-content">
          <span className="sw-hero-overline">Soluciones Digitales</span>
          <h1>Software Especializado<br />de Pesaje</h1>
          <p>
            Conectamos el pesaje industrial con sus sistemas de gestión: control
            en tiempo real, trazabilidad completa e integración con ERP.
          </p>
        </div>
      </section>

      {/* ── Products ── */}
      <section className="sw-products">

        <div className="sw-products-intro">
          <span className="sw-label">Nuestros Productos</span>
          <h2 className="sw-section-title">Software diseñado para cada proceso</h2>
          <p className="sw-section-sub">
            Desarrollamos soluciones para conectar el dato de peso con cada
            etapa de su proceso productivo y sistema de gestión.
          </p>
        </div>

        {softwareProducts.map((sw, i) => {
          const isReverse = i % 2 !== 0;
          return (
            <div
              key={sw.id}
              className={`sw-row-stripe${isReverse ? ' sw-row-stripe--alt' : ''}`}
            >
              <div className={`sw-row${isReverse ? ' sw-row--reverse' : ''}`}>

                {/* Photo panel */}
                <div className="sw-row-photo" role="img" aria-label={sw.imageAlt}>
                  <img src={sw.image} alt="" loading="lazy" />
                  <div className="sw-row-photo-vignette" aria-hidden="true" />
                </div>

                {/* Content panel */}
                <div className="sw-row-content">
                  <h3 className="sw-row-title">{sw.title}</h3>
                  <p className="sw-row-tagline">{sw.tagline}</p>
                  <ul className="sw-row-features">
                    {sw.features.map((f) => (
                      <li key={f}>
                        <span className="sw-row-check" aria-hidden="true">
                          <IconCheck />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>
          );
        })}
      </section>

      {/* ── Stats ── */}
      <section className="sw-stats">
        <div className="sw-stats-grid">
          <div className="sw-stat">
            <span className="sw-stat-num">2</span>
            <span className="sw-stat-label">Software Comercial</span>
          </div>
          <div className="sw-stat">
            <span className="sw-stat-num">+10</span>
            <span className="sw-stat-label">Tipos Software a la medida</span>
          </div>
          <div className="sw-stat">
            <span className="sw-stat-num">SQL Server</span>
            <span className="sw-stat-label">Base Datos</span>
          </div>
          <div className="sw-stat">
            <span className="sw-stat-num">Tiempo real</span>
            <span className="sw-stat-label">Control y monitoreo</span>
          </div>
        </div>
      </section>

      {/* ── Why us ── */}
      <section className="sw-why">
        <div className="container">
          <div className="sw-why-header">
            <span className="sw-label">Nuestra Diferencia</span>
            <h2 className="sw-section-title">¿Por qué elegir nuestro software?</h2>
            <p className="sw-section-sub">
              Diseñado específicamente para entornos de pesaje industrial, con el
              respaldo técnico de más de 30 años en el sector.
            </p>
          </div>

          <div className="sw-why-grid">

            <div className="sw-why-card">
              <div className="sw-why-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <div className="sw-why-body">
                <h3>Datos en tiempo real</h3>
                <p>Monitoreo continuo de todas las básculas conectadas con alertas automáticas ante desviaciones del proceso.</p>
              </div>
            </div>

            <div className="sw-why-card">
              <div className="sw-why-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <div className="sw-why-body">
                <h3>Trazabilidad certificada</h3>
                <p>Registros auditables que cumplen con normativas BPM, HACCP e ISO para sectores regulados.</p>
              </div>
            </div>


            <div className="sw-why-card">
              <div className="sw-why-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </div>
              <div className="sw-why-body">
                <h3>Configuración a la medida</h3>
                <p>Parametrización de fórmulas, recetas, límites y reportes sin necesidad de programación personalizada.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="sw-cta">
        <div className="container">
          <h2>¿Listo para digitalizar su proceso de pesaje?</h2>
          <p>
            Cuéntenos sobre su proceso y le mostraremos cómo nuestro software
            se integra con su operación existente.
          </p>
          <div className="sw-cta-actions">
            <Link to="/contacto" className="btn-primary">Solicitar Demostración</Link>
            <a href="tel:+523336349050" className="btn-outline">Llamar: (33) 3634-9050</a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Software;
